import * as React from 'react';

import { useSlug } from '../hooks/useSlug';
import { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './styles.module.css';

export type ToggleProps = GlobalProps & {
  disabled?: boolean;
  invert?: boolean;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onToggle?: React.MouseEventHandler<HTMLButtonElement>;
  active?: boolean;
  dataAttributes?: Record<string, string>;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
};

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  function ToggleComponent(props, ref) {
    const {
      onToggle,
      onClick,
      active,
      disabled = false,
      invert = false,
      name = 'toggle',
      dataAttributes = {},
      title = 'toggle button',
      buttonProps,
    } = props;

    const validDataAttributes = Object.fromEntries(
      Object.entries(dataAttributes).filter(([prop]) => /^data-/.test(prop))
    );

    const [slug] = useSlug(name);

    const cx = classes(
      [styles.toggle, { [styles.active]: active, [styles.invert]: invert }],
      props
    );

    const cy = classes(
      [styles.circle, { [styles.active]: active, [styles.invert]: invert }],
      {
        ...props,
        name: `${name}-inner`,
      }
    );

    const handleClick = React.useCallback(
      (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.persist();
        if (onToggle) {
          onToggle(ev);
        }
        if (onClick) {
          onClick(ev);
        }
      },
      [onClick, onToggle]
    );

    return (
      <button
        {...validDataAttributes}
        data-component={slug}
        disabled={disabled}
        role="button"
        title={title}
        aria-label={title}
        aria-pressed={active}
        type="button"
        ref={ref}
        className={cx}
        onClick={handleClick}
        {...buttonProps}
      >
        <div className={cy} />
      </button>
    );
  }
);
